import RoutesRoot from '@/router/routes/RoutesRoot.js';
import RoutesLogin from '@/router/routes/authentication/RoutesLogin.js';
import RoutesAthentication from '@/router/routes/RoutesAthentication.js';
import { Account } from '@/services/Api/index';
import StorageHelper from '@/utils/storageHelper';
import AccountMixin from '@/utils/accountUtils.js';
import { clearAppData } from '@/mixins/storageUtils.js';
import { UserTokenRoles } from '@/constants/userTokenRoles.js';
import { getRecaptchaSiteKey } from '@/constants/recaptcha.constants.js';
import { extractErrorMessage } from '../utils/errorExtractor';

const LoginMixin = {
  mixins: [AccountMixin],
  methods: {
    async processLogin(
      { taxNumber, customerNumber, phoneNumber, password, firstLogin },
      recaptchaToken = undefined,
    ) {
      // Check the submit where it comes from
      let loginType;
      let number;
      let storageFieldName;
      let eventLoginType = 'login';
      if (taxNumber) {
        loginType = 'loginWithVKN';
        storageFieldName = 'VUE_APP_STORAGE_CURRENT_USER_VKN_KEY';
        number = taxNumber;
        eventLoginType += '_vkn';
      } else if (customerNumber) {
        loginType = 'loginWithCustomerCode';
        storageFieldName = 'VUE_APP_STORAGE_CURRENT_USER_CN_KEY';
        number = customerNumber;
        eventLoginType += '_customer';
      } else {
        loginType = 'loginWithPhoneNumber';
        storageFieldName = 'VUE_APP_STORAGE_CURRENT_USER_PN_KEY';
        number = phoneNumber;
        if (firstLogin) {
          eventLoginType += '_first';
        } else {
          eventLoginType += '_phone';
        }
      }

      // Send login type to Account service
      const loginRes = await Account[loginType](number, password, recaptchaToken);
      const { Data } = loginRes ? loginRes.data : { Data: null };

      if (!Data) {
        return;
      }
      const isIntermediateStep =
        Data.tokenRole === UserTokenRoles.UnSelectedUser ||
        Data.tokenRole === UserTokenRoles.VerifyMFA ||
        Data.tokenRole === UserTokenRoles.RegisterMFA;

      if (!isIntermediateStep) {
        await this.$store.dispatch('auth/setAuthData', Data);
      }

      // Fetch the storage field which number user sent
      new StorageHelper({
        id: process.env[storageFieldName],
      }).set(this.rememberMe ? number : null);

      const { token, refreshToken, user, pos, staffValidation } = Data;

      const previousToken = new StorageHelper({
        id: process.env.VUE_APP_STORAGE_TOKEN_KEY,
      }).get();

      if (token) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_TOKEN_KEY }).set(token);
      }
      if (Data.tokenRole === UserTokenRoles.UnSelectedUser) {
        await this.$store.dispatch('auth/setUserPhones', Data.userPhones);
        this.$router.push(RoutesLogin.SelectPhone.path);
        return;
      }

      if (
        Data.tokenRole === UserTokenRoles.VerifyMFA ||
        Data.tokenRole === UserTokenRoles.RegisterMFA
      ) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_PRE_MFA_TOKEN }).set(previousToken);

        const phone =
          (Data.user && Data.user.phoneNumber) ||
          (loginType === 'loginWithPhoneNumber' ? number : '');
        const last4 = String(phone)
          .replace(/\D/g, '')
          .slice(-4);
        this.$router.push({
          path: RoutesLogin.LoginMfaVerification.path,
          query: { mfaRole: Data.tokenRole, maskedPhone: last4, mfaOrigin: this.$route.name },
        });
        return;
      }

      if (Data.tokenRole === UserTokenRoles.ExpiredPassword) {
        this.$router.push({ name: 'expired-password', params: { isAuth: true } });
        return;
      }

      if (refreshToken) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_REFRESH_TOKEN_KEY }).set(
          refreshToken.token,
        );
      }
      if (user) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_CURRENT_USER_KEY }).set(user);
      }

      if (pos) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_LOGIN_TYPE }).set(eventLoginType);
        const posArr = Array.isArray(pos) ? pos : [pos];
        await this.$store.dispatch('auth/setPosList', posArr);

        if (posArr.length > 1) {
          this.$router.push(
            `${RoutesRoot.Authentication.path}/${RoutesAthentication.Login.path}/${RoutesLogin.SelectPos.path}`,
          );
        } else {
          new StorageHelper({ id: process.env.VUE_APP_STORAGE_CURRENT_POS_KEY }).set(posArr[0]);
          this.accountLogin(posArr[0].posChannel, staffValidation, user);
        }
      }

      if (!staffValidation) {
        // Added this to remove for google password managers.
        [
          'input[name=password]',
          'input[name=phoneNumber]',
          'input[name=customerNumber]',
          'input[name=vkn]',
        ].forEach(item => {
          if (document.querySelector(item)) {
            document.querySelector(item).remove();
          }
        });
      }
    },
    async handleLoginRequest() {
      const isValid = await this.$refs.form.$refs.observer.validate();
      if (!isValid || this.$store.state.auth.loginState.fetching) return;
      this.submitted = true;
      let recaptchaToken = undefined;

      this.$store.dispatch('auth/setLoginFetching', true);
      this.$store.dispatch('auth/setLoginError', null);

      try {
        recaptchaToken = await grecaptcha.enterprise.execute(getRecaptchaSiteKey(), {
          action: 'login',
        });
      } catch (error) {
        console.error('reCAPTCHA execute failed:', error?.message);
      }

      try {
        await this.processLogin(this.formModel, recaptchaToken);
      } catch (error) {
        this.$store.dispatch('auth/setLoginError', error);

        const errModalConfig = {
          isModalOpened: true,
          modalText: extractErrorMessage(error),
        };
        this.$store.dispatch('app/setErrorModalConfig', errModalConfig);
      } finally {
        this.$store.dispatch('auth/setLoginFetching', false);
      }
    },
    processLogout() {
      clearAppData(
        'logout',
        `${RoutesRoot.Authentication.path}/${RoutesAthentication.Login.path}/${RoutesLogin.SelectLoginType.path}`,
      );
    },
  },
};

export default LoginMixin;
